import { Injectable } from '@angular/core'
import { FormGroup } from '@angular/forms'
import { BehaviorSubject } from 'rxjs'
import { APPLICATION_STATE } from 'shared-lib'
import { IAutoQuestionFormData } from './automation-auto-question/auto-questions.model'

export type AutoQuestionSwimlaneFields = keyof Pick<IAutoQuestionFormData, 'swimlane' | 'afterAnsweredSwimlane' | 'afterSendSwimlane'>

export type AutomationSwimlaneChange = {
  swimlane: APPLICATION_STATE
  section: AutoQuestionSwimlaneFields
}

export const SUPPORTED_AUTOMATION_SWIMLANES = [
  APPLICATION_STATE.APPLIED,
  APPLICATION_STATE.SCREENED,
  APPLICATION_STATE.INTERVIEWING,
  APPLICATION_STATE.FINALIST,
]

const FIELDS_ORDER: AutoQuestionSwimlaneFields[] = ['swimlane', 'afterSendSwimlane', 'afterAnsweredSwimlane']
@Injectable({
  providedIn: 'root',
})
export class AutomationSwimlaneChangeService {
  private selectedSwimlanes: Record<AutoQuestionSwimlaneFields, APPLICATION_STATE | null> = {
    swimlane: APPLICATION_STATE.APPLIED as APPLICATION_STATE | null,
    afterSendSwimlane: null as APPLICATION_STATE | null,
    afterAnsweredSwimlane: null as APPLICATION_STATE | null,
  }

  private availableSwimlanesSubject = new BehaviorSubject<{
    [key in AutoQuestionSwimlaneFields]: APPLICATION_STATE[]
  }>({
    swimlane: SUPPORTED_AUTOMATION_SWIMLANES,
    afterSendSwimlane: [],
    afterAnsweredSwimlane: [],
  })

  private requestMoreSwimlaneSubject = new BehaviorSubject<APPLICATION_STATE | null>(null)

  availableSwimlanes$ = this.availableSwimlanesSubject.asObservable()
  requestMoreSwimlane$ = this.requestMoreSwimlaneSubject.asObservable()

  swimlaneChangeEvent(form: FormGroup, event: Record<AutoQuestionSwimlaneFields, APPLICATION_STATE | null>): void {
    if (event.swimlane !== this.selectedSwimlanes.swimlane) {
      this.selectedSwimlanes.afterSendSwimlane = null
      this.selectedSwimlanes.afterAnsweredSwimlane = null
      form.patchValue({
        afterSendSwimlane: null,
        afterAnsweredSwimlane: null,
      })
    } else if (event.afterSendSwimlane !== this.selectedSwimlanes.afterSendSwimlane) {
      this.selectedSwimlanes.afterAnsweredSwimlane = null
      form.patchValue({
        afterAnsweredSwimlane: null,
      })
    }

    this.selectedSwimlanes = event
    const newAvailableSwimlanes = {
      swimlane: this.getAvailableAutomationSwimlanes('swimlane'),
      afterSendSwimlane: this.getAvailableAutomationSwimlanes('afterSendSwimlane'),
      afterAnsweredSwimlane: this.getAvailableAutomationSwimlanes('afterAnsweredSwimlane'),
    }

    this.availableSwimlanesSubject.next(newAvailableSwimlanes)
  }

  requestMoreSwimlaneChangeEvent(newSwimlane: APPLICATION_STATE): void {
    this.requestMoreSwimlaneSubject.next(newSwimlane)
  }

  private getPreviousSection(currentSection: AutoQuestionSwimlaneFields): AutoQuestionSwimlaneFields {
    const currentIndex = FIELDS_ORDER.indexOf(currentSection)
    return FIELDS_ORDER[currentIndex - 1]
  }

  private getAvailableAutomationSwimlanes(forSection: AutoQuestionSwimlaneFields): APPLICATION_STATE[] {
    if (forSection === 'swimlane') {
      return SUPPORTED_AUTOMATION_SWIMLANES
    }

    const previousSection = this.getPreviousSection(forSection)
    const previousSwimlane = this.selectedSwimlanes[previousSection]

    if (this.selectedSwimlanes.swimlane && !previousSwimlane && forSection === 'afterAnsweredSwimlane') {
      const swimlaneSelected = this.selectedSwimlanes.swimlane
      const swimlaneIndex = SUPPORTED_AUTOMATION_SWIMLANES.indexOf(swimlaneSelected)
      return SUPPORTED_AUTOMATION_SWIMLANES.filter((_, index) => index > swimlaneIndex)
    }

    if (!previousSwimlane) return []

    const orderedStates = SUPPORTED_AUTOMATION_SWIMLANES
    const previousIndex = orderedStates.indexOf(previousSwimlane)

    return SUPPORTED_AUTOMATION_SWIMLANES.filter(swimlane => {
      const swimlaneIndex = orderedStates.indexOf(swimlane)
      return swimlaneIndex > previousIndex
    })
  }
}
